import { useEffect, useRef, useState } from 'react';
import { BaseLineMusic } from '@/app/components/icons/BaseLineMusic';
import { BaseLineReferenceVideo } from '@/app/components/icons/BaseLineReferenceVideo';
import { BaseLineStyleTransfer } from '@/app/components/icons/BaseLineStyleTransfer';
import { ProductLineElement } from '@/app/components/icons/ProductLineElement';
import { ProductLinePic } from '@/app/components/icons/ProductLinePic';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { useStore } from '@/app/providers/store';
import type { TaskInputPrompts } from '@/app/types';
import { cn } from '@/app/utils';

export const hasTextWithTagsContent = (text?: string, prompts?: TaskInputPrompts[]) =>
  Boolean(
    text ||
      prompts?.some((prompt) => prompt.type === 'remix_style' && (prompt.remix_style?.name || prompt.remix_style?.id)),
  );

export const TextWithTags = ({
  text,
  prompts,
  onClick,
  tagClassName,
}: {
  text: string;
  prompts?: TaskInputPrompts[];
  onClick?: () => void;
  tagClassName?: string;
}) => {
  const { openReferenceModalWithData } = useStore((state) => ({
    openReferenceModalWithData: state.openReferenceModalWithData,
  }));

  const containerRef = useRef<HTMLDivElement>(null);
  const [renderedElements, setRenderedElements] = useState<React.ReactNode[]>([]);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const remixStylePrompts = prompts?.filter((prompt) => prompt.type === 'remix_style') ?? [];
    const remixStyleTags = remixStylePrompts
      .map((prompt) => prompt.remix_style?.name || prompt.remix_style?.id)
      .filter((label): label is string => Boolean(label));
    const renderRemixStyleTags = () =>
      remixStyleTags.map((label, index) => (
        <span
          key={`remix-style-${index}-${label}`}
          className={cn(
            'tag-element inline-flex items-center whitespace-nowrap rounded-6 border border-system-white12 bg-system-black/40 px-1.5 py-0.5 align-bottom text-system-white/80 text-xs',
            index < remixStyleTags.length - 1 || text ? 'mr-1' : undefined,
            tagClassName,
          )}
        >
          <BaseLineStyleTransfer className="mr-0.5 size-3 shrink-0 text-system-white" />
          <span className="max-w-[80px] whitespace-normal">
            <CustomTooltip content={label} side="top" showOnlyOnOverflow={true} maxLines={1}>
              {label}
            </CustomTooltip>
          </span>
        </span>
      ));

    if (!prompts || prompts.length === 0) {
      setRenderedElements([text]);
      return;
    }

    // 提取所有 reference 类型的 prompts
    const referenceList = prompts.filter((prompt) => ['image', 'material', 'video', 'audio'].includes(prompt.type));
    // 如果没有 reference 类型的 prompts，直接返回原文本
    if (referenceList.length === 0) {
      setRenderedElements([...renderRemixStyleTags(), text]);
      return;
    }

    // 使用正则表达式找出所有匹配 [@xxx] 的部分
    const tagRegex = /\[@([^\]]+)\]/g;
    const matches = [];
    let match = tagRegex.exec(text);

    while (match) {
      const tagName = match[1]; // 捕获括号内的名称
      const currentReference = referenceList.find((it) => it.name === tagName);
      // 只有当标签名在 referenceList 中存在时才添加到匹配列表，过滤掉非material类型的标签，如图片
      if (currentReference) {
        matches.push({
          forbidden: !currentReference.material?.id, // 完整匹配的文本，包括方括号
          fullMatch: match[0], // 捕获组内容，不包括方括号
          id: currentReference.material?.id,
          index: match.index,
          isShare: !!currentReference.material?.share_id && currentReference.material.share_id !== '0',
          length: match[0].length,
          tagContent: tagName,
          type: currentReference.type, // 匹配开始位置
          version: currentReference.material?.version, // 匹配长度
          creator_id: currentReference.material?.creator_id,
        });
      }
      match = tagRegex.exec(text);
    }
    // 如果没有匹配项，直接返回原文本
    if (matches.length === 0) {
      setRenderedElements([...renderRemixStyleTags(), text]);
      return;
    }
    setVisible(false);
    // 构建初始JSX元素数组
    const elements: React.ReactNode[] = renderRemixStyleTags();
    let lastIndex = 0;

    matches.forEach((match, idx) => {
      const startIndex = match.index;
      const isFirstTag = idx === 0 && startIndex === 0; // 是否是文本开头的第一个标签
      const isPrevTag = startIndex === lastIndex; // 是否紧跟前一个标签（无文本间隔）

      if (startIndex > lastIndex) {
        elements.push(text.substring(lastIndex, startIndex));
      }

      // 计算标签的样式类
      const tagClasses = cn(
        'tag-element inline-flex rounded-6 bg-system-black/40 px-1.5 text-system-white/80 whitespace-nowrap text-xs border border-system-white12 py-0.5 items-center align-bottom mr-1',
        // 根据位置决定左右边距
        {
          'cursor-pointer': match.type === 'material' && !match.forbidden, // 普通标签左间距4px
          'ml-1': !isFirstTag && !isPrevTag,
          'text-system-white64': match.forbidden,
        },
        tagClassName,
      );

      // 初始渲染不考虑行首，标记每个标签以便后期处理
      elements.push(
        <span
          key={`${match.tagContent}-${idx}`}
          data-tag-id={idx}
          className={tagClasses}
          onClick={() => {
            if (match.type === 'material' && !match.forbidden) {
              onClick?.();
              openReferenceModalWithData({
                element: {
                  id: match.id,
                  name: match.tagContent,
                  version: match.isShare ? undefined : match.version,
                  modality: 'image',
                  creator_id: match.creator_id,
                },
                referenceModalFrom: 'community',
                referenceModalMode: 'view',
                referenceModalOpen: true,
                referenceModalType: match.isShare ? 'share' : 'reference',
              });
            }
          }}
        >
          {match.type === 'material' && (
            <ProductLineElement
              className={cn(
                'mr-0.5 size-3 flex-shrink-0 text-system-blue02',
                match.forbidden && 'text-system-white',
                !match.forbidden && match.isShare && 'text-[#AB8DFF]',
              )}
            />
          )}
          {match.type === 'image' && <ProductLinePic className={cn('mr-0.5 size-3 flex-shrink-0 text-system-white')} />}
          {match.type === 'video' && (
            <BaseLineReferenceVideo className={cn('mr-0.5 size-3 flex-shrink-0 text-system-white')} />
          )}
          {match.type === 'audio' && <BaseLineMusic className={cn('mr-0.5 size-3 flex-shrink-0 text-system-white')} />}
          <div className="max-w-[80px] whitespace-normal">
            <CustomTooltip content={match.tagContent} side="top" showOnlyOnOverflow={true} maxLines={1}>
              {match.tagContent}
            </CustomTooltip>
          </div>
        </span>,
      );

      lastIndex = startIndex + match.length;
    });
    // 添加最后一个匹配后的文本（如果有）
    if (lastIndex < text.length) {
      elements.push(text.substring(lastIndex));
    }

    setRenderedElements(elements);

    // DOM渲染后检查标签是否在行首
    setTimeout(() => {
      if (containerRef.current) {
        const tagElements = containerRef.current.querySelectorAll('.tag-element');

        tagElements.forEach((tag) => {
          const tagRect = tag.getBoundingClientRect();
          const containerRect = containerRef.current?.getBoundingClientRect();
          if (containerRect) {
            // 检查标签左侧是否接近容器左侧（考虑一定的容差）
            const isLineStart = Math.abs(tagRect.left - containerRect.left) < 8;

            if (isLineStart) {
              // 如果是行首，移除左边距
              tag.classList.remove('ml-1');
            }
          }
        });

        setVisible(true);
      }
    }, 0);
  }, [text, prompts, tagClassName]);

  if (!text && renderedElements.length === 0) return null;

  return (
    <span ref={containerRef} className={cn('whitespace-normal', visible ? 'opacity-100' : 'opacity-0')}>
      {renderedElements}
    </span>
  );
};
